import { ColDef, ColGroupDef } from '@ag-grid-community/core';
import { HeaderRendererComponent } from '@frk/eds-components';
import {
  AgGridHelperService,
  ProductColGroupId,
  ProductColId,
} from '@products/fund-listing/services/ag-grid-helper.service';
import { EMDASH } from '@products/utils/constants/product.constants';
import { TranslateService } from '@shared/translate/translate.service';
import { ProductData, SuppressDetails } from '@types';
import {
  FundListingIdentifiersController,
  FundListingPerformanceController,
  FundListingRatingsController,
} from '../controllers';

/**
 * Return default identifier column definitions for Canada.
 * @param agGridHelperService ag grid helper service instance
 * @param translateService translate service instance
 */
export function getDefaultIdentifierColumnDefsForCA(
  listingController: FundListingIdentifiersController,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColDef[] {
  const columnDefs = [
    agGridHelperService.getFundNameColDef(),
    {
      colId: ProductColId.CURRENCY_COL,
      headerName: translateService.instant('pds.MARKET_CURRENCY'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.shareClassCurrency;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.FUND_CODE,
      // headerName: translateService.instant('pds.FUND_CODE'), // Check with PDS Label
      headerName: translateService.instant('products.ppss-fi-fund-code'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        footnotePlacement: `${agGridHelperService.getConfigurationNameForPlacement(
          listingController.productType
        )}FundCodePPSS`,
      },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.taid
          ? product.shareClass?.identifiers.taid
          : EMDASH;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.FRONT_END,
      // headerName: translateService.instant('pds.FRONT_END'), // Check with PDS Label
      headerName: translateService.instant('products.ppss-fi-front-end'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        footnotePlacement: `${agGridHelperService.getConfigurationNameForPlacement(
          listingController.productType
        )}FrontEndPPSS`,
      },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.frontend
          ? product.shareClass?.identifiers.frontend
          : EMDASH;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.LOW_LOAD,
      // headerName: translateService.instant('pds.LOW_LOAD'), // Check with PDS Label
      headerName: translateService.instant('products.ppss-fi-low-load'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        footnotePlacement: `${agGridHelperService.getConfigurationNameForPlacement(
          listingController.productType
        )}LowLoadPPSS`,
      },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.lowload
          ? product.shareClass?.identifiers.lowload
          : EMDASH;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.DSC,
      // headerName: translateService.instant('pds.DSC'), // Check with PDS Label
      headerName: translateService.instant('products.ppss-fi-dsc'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        footnotePlacement: `${agGridHelperService.getConfigurationNameForPlacement(
          listingController.productType
        )}DscPPSS`,
      },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.dsc
          ? product.shareClass?.identifiers.dsc
          : EMDASH;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: ProductColId.ADM,
      // headerName: translateService.instant('pds.ADM'), // Check with PDS Label
      headerName: translateService.instant('products.ppss-fi-adm'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        footnotePlacement: `${agGridHelperService.getConfigurationNameForPlacement(
          listingController.productType
        )}AdmPPSS`,
        tooltipContent: translateService.tooltip('products.ppss-fi-adm'),
      },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.adm
          ? product.shareClass?.identifiers.adm
          : EMDASH;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
  ];
  return columnDefs;
}

/**
 * Return default rating column definitions for intl.
 * @param listingController ppss tab component
 * @param agGridHelperService ag grid helper service instance
 */
export function getDefaultRatingColumnDefsForCA(
  listingController: FundListingRatingsController,
  agGridHelperService: AgGridHelperService
): ColGroupDef[] {
  return [
    {
      groupId: ProductColGroupId.FUND,
      children: [agGridHelperService.getFundNameColDef()],
    },
    {
      groupId: ProductColGroupId.CURRENCY,
      children: [agGridHelperService.getCurrencyColDef()],
    },
    {
      groupId: ProductColGroupId.MORNINGSTAR,
      headerGroupComponent: 'ratingsGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      headerClass: 'ag-header-group-cell--noflex',
      children: [
        agGridHelperService.getMorningStarCategoryColDef(listingController),
        agGridHelperService.getMorningStarOverallRatingColDef(
          listingController
        ),
        agGridHelperService.getMorningStar3yrRatingColDef(),
        agGridHelperService.getMorningStar5yrRatingColDef(),
        agGridHelperService.getMorningStar10yrRatingColDef(),
      ],
    },
  ];
}

/**
 * Return ETF rating column definitions for CA.
 * @param listingController ppss tab component
 * @param agGridHelperService ag grid helper service instance
 */
export function getRatingColumnDefsForCA_ETF(
  listingController: FundListingRatingsController,
  agGridHelperService: AgGridHelperService
): ColGroupDef[] {
  return [
    {
      groupId: ProductColGroupId.FUND,
      children: [
        agGridHelperService.getTickerColDef(),
        agGridHelperService.getFundNameColDef(),
      ],
    },
    {
      groupId: ProductColGroupId.CURRENCY,
      children: [agGridHelperService.getCurrencyColDef()],
    },
    {
      groupId: ProductColGroupId.MORNINGSTAR,
      headerGroupComponent: 'ratingsGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      headerClass: 'ag-header-group-cell--noflex',
      children: [
        agGridHelperService.getMorningStarCategoryColDef(listingController),
        agGridHelperService.getMorningStarOverallRatingColDef(
          listingController
        ),
        agGridHelperService.getMorningStar3yrRatingColDef(),
        agGridHelperService.getMorningStar5yrRatingColDef(),
        agGridHelperService.getMorningStar10yrRatingColDef(),
      ],
    },
  ];
}

/**
 * Return performance column definitions for Canada ETF.
 * @param listingController ppss tab component
 * @param state ppss service state
 * @param agGridHelperService ag grid helper service instance
 */
export function getPerformanceColumnDefsForCA_ETF(
  listingController: FundListingPerformanceController,
  suppressDetails: SuppressDetails,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: ProductColGroupId.FUND,
      headerGroupComponent: 'calcTypeGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      children: [
        agGridHelperService.getTickerColDef(),
        agGridHelperService.getFavFundColDef(listingController),
        agGridHelperService.getFundNameColDef(),
      ],
    },
    {
      groupId: ProductColGroupId.NAV,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => listingController.navAsOfDate,
      },
      children: [
        agGridHelperService.getNavValueColDef(listingController, {
          width: 80,
        }),
        agGridHelperService.getMarketPriceValueColDef(listingController, {
          width: 80,
        }),
        agGridHelperService.getNavChangeValueColDef(listingController, {
          width: 80,
        }),
        agGridHelperService.getMarketPriceChangeValueColDef(listingController, {
          width: 80,
        }),
        agGridHelperService.getNavYTDReturnColDef(listingController, {
          headerName: translateService.instant(
            'products.ytd-total-returns-nav'
          ),
          width: 80,
        }),
        agGridHelperService.getMarketPriceYTDReturnColDef(listingController, {
          headerName: translateService.instant(
            'products.ytd-total-returns-market-price'
          ),
          width: 95,
        }),
      ],
    },
    {
      groupId: ProductColGroupId.PERFORMANCE,
      headerGroupComponent: 'performanceGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      children: [
        agGridHelperService.getAvgAnnualMonthly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getAvgAnnualMonthly3YrColDef(listingController, {
          width: 80,
        }),
        agGridHelperService.getAvgAnnualMonthly5YrColDef(listingController, {
          width: 80,
        }),
        agGridHelperService.getAvgAnnualMonthly10YrColDef(listingController, {
          width: 80,
        }),
        agGridHelperService.getAvgAnnualMonthlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width: 80,
          },
          false
        ),
        agGridHelperService.getAvgAnnualQuarterly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 80,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly3YrColDef(listingController, {
          width: 80,
        }),
        agGridHelperService.getAvgAnnualQuarterly5YrColDef(listingController, {
          width: 80,
        }),
        agGridHelperService.getAvgAnnualQuarterly10YrColDef(listingController, {
          width: 80,
        }),
        agGridHelperService.getAvgAnnualQuarterlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width: 80,
          },
          false
        ),
        agGridHelperService.getAvgAnnualInceptionDateColDef(listingController, {
          width: 80,
        }),
        agGridHelperService.getCalendarYearMonthly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCalendarYearMonthly2YrColDef(listingController),
        agGridHelperService.getCalendarYearMonthly3YrColDef(listingController),
        agGridHelperService.getCalendarYearMonthly4YrColDef(listingController),
        agGridHelperService.getCalendarYearMonthly5YrColDef(listingController),
        agGridHelperService.getCalendarYearQuarterly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCalendarYearQuarterly2YrColDef(
          listingController
        ),
        agGridHelperService.getCalendarYearQuarterly3YrColDef(
          listingController
        ),
        agGridHelperService.getCalendarYearQuarterly4YrColDef(
          listingController
        ),
        agGridHelperService.getCalendarYearQuarterly5YrColDef(
          listingController
        ),
        agGridHelperService.getCalendarYearInceptionColDef(),
        agGridHelperService.getCumulativeMonthly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCumulativeMonthly3YrColDef(listingController),
        agGridHelperService.getCumulativeMonthly5YrColDef(listingController),
        agGridHelperService.getCumulativeMonthly10YrColDef(listingController),
        agGridHelperService.getCumulativeMonthlyInceptionColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCumulativeQuarterly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCumulativeQuarterly3YrColDef(listingController),
        agGridHelperService.getCumulativeQuarterly5YrColDef(listingController),
        agGridHelperService.getCumulativeQuarterly10YrColDef(listingController),
        agGridHelperService.getCumulativeQuarterlyInceptionColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getDiscreteMonthly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getDiscreteMonthly2YrColDef(listingController),
        agGridHelperService.getDiscreteMonthly3YrColDef(listingController),
        agGridHelperService.getDiscreteMonthly4YrColDef(listingController),
        agGridHelperService.getDiscreteMonthly5YrColDef(listingController),
        agGridHelperService.getDiscreteQuarterly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getDiscreteQuarterly2YrColDef(listingController),
        agGridHelperService.getDiscreteQuarterly3YrColDef(listingController),
        agGridHelperService.getDiscreteQuarterly4YrColDef(listingController),
        agGridHelperService.getDiscreteQuarterly5YrColDef(listingController),
        agGridHelperService.getDiscreteInceptionColDef(),
        agGridHelperService.getFundDocumentColDef(),
      ],
    },
  ];
  return colGroupDefs;
}
